import axios from "axios"
import { getUrl } from "../../utility/helpers/url"
import { store } from "../../redux/storeConfig/store"

/**
 * @class ValidationLocationService
 */
class ValidationLocationService {
  constructor(config) {
    this.config = config
  }

  /**
   * Get the appropriate URL based on user role
   *
   * @param {string} jobSiteId
   * @param {string} companyId
   * @returns {string}
   */
  getLocationUrl(jobSiteId, companyId) {
    const state = store.getState()
    const userData = state.auth.userData
    const role = userData?.role

    return role === "admin"
      ? `/admin/company/${companyId}/job-site/${jobSiteId}/location`
      : `/admin/job-site/${jobSiteId}/location`
  }

  /**
   * Get list of validation locations
   *
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async listLocations({ companyId, jobSiteId }) {
    try {
      const response = await axios.get(
        getUrl(this.getLocationUrl(jobSiteId, companyId))
      )
      return response.data
    } catch (error) {
      throw error
    }
  }

  /**
   * Create a new validation location
   *
   * @param {object} data
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async createLocation(data, { companyId, jobSiteId }) {
    try {
      const response = await axios.post(
        getUrl(this.getLocationUrl(jobSiteId, companyId)),
        data
      )
      return response.data
    } catch (error) {
      console.error("Failed to create validation location:", error)
      throw new Error(`Failed to create validation location: ${error.message}`)
    }
  }

  /**
   * Update a validation location
   *
   * @param {number} locationId
   * @param {object} data
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async updateLocation(locationId, data, { companyId, jobSiteId }) {
    try {
      const response = await axios.put(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}`),
        data
      )
      return response.data
    } catch (error) {
      console.error("Failed to update validation location:", error)
      throw new Error(`Failed to update validation location: ${error.message}`)
    }
  }

  /**
   * Delete a validation location
   *
   * @param {number} locationId
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async deleteLocation(locationId, { companyId, jobSiteId }) {
    try {
      const response = await axios.delete(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}`)
      )
      return response.data
    } catch (error) {
      console.error("Failed to delete validation location:", error)
      throw new Error(`Failed to delete validation location: ${error.message}`)
    }
  }

  /**
   * Get QR validation codes for a location
   *
   * @param {number} locationId
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async listQRValidationCodes(locationId, { companyId, jobSiteId }) {
    try {
      const response = await axios.get(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}/validation-codes`)
      )
      return response.data
    } catch (error) {
      console.error("Failed to fetch QR validation codes:", error)
      throw new Error(`Failed to fetch QR validation codes: ${error.message}`)
    }
  }

  /**
   * Create a new QR validation code
   *
   * @param {number} locationId
   * @param {object} data
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async createQRValidationCode(locationId, data, { companyId, jobSiteId }) {
    try {
      const response = await axios.post(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}/validation-codes`),
        data
      )
      return response.data
    } catch (error) {
      console.error("Failed to create QR validation code:", error)
      throw error
    }
  }

  /**
   * Update a QR validation code
   *
   * @param {number} locationId
   * @param {number} codeId
   * @param {object} data
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async updateQRValidationCode(locationId, codeId, data, { companyId, jobSiteId }) {
    try {
      const response = await axios.put(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}/validation-codes/${codeId}`),
        data
      )
      return response.data
    } catch (error) {
      console.error("Failed to update QR validation code:", error)
      throw error
    }
  }

  /**
   * Delete a QR validation code
   *
   * @param {number} locationId
   * @param {number} codeId
   * @param {object} params
   * @returns {Promise<boolean|T>}
   */
  async deleteQRValidationCode(locationId, codeId, { companyId, jobSiteId }) {
    try {
      const response = await axios.delete(
        getUrl(`${this.getLocationUrl(jobSiteId, companyId)}/${locationId}/validation-codes/${codeId}`)
      )
      return response.data
    } catch (error) {
      console.error("Failed to delete QR validation code:", error)
      throw new Error(`Failed to delete QR validation code: ${error.message}`)
    }
  }
}

export default ValidationLocationService
